import { config as appConfig } from '@/core'
import { LDEvaluationDetail, useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect, useState } from 'react'

type Context = {
  kind: string
  key: string
  name?: string
  custom?: Record<string, string>
}

const createTenantContext = (subdomain: string): Context => ({
  kind: 'tenant',
  key: subdomain,
  name: subdomain,
  custom: { subdomain },
})

const isLocalEnv = appConfig.app.env === 'local'

/**
 * For manual testing in local only; deployed environments ignore it. Local development runs
 * without LaunchDarkly, so every flag reads as on. To see a screen with a flag off, add it here
 * (`{ 'flag-key': false }`) and remove it before committing: useFeatureFlag.local.test.tsx fails
 * while the map is not empty.
 */
export const LOCAL_FLAG_OVERRIDES: Record<string, boolean> = {}

/**
 * Reads a boolean LaunchDarkly flag for the tenant context. The served value counts whatever the
 * evaluation reason:
 * - RULE_MATCH: Flag has targeting rules that matched the context
 * - FALLTHROUGH: Flag uses the Default rule (no targeting rules or no rules matched)
 * - TARGET_MATCH: Specific context was individually targeted
 * - OFF: Flag is turned off for all contexts
 *
 * @param flagKey - The LaunchDarkly feature flag key
 * @param subdomain - The tenant subdomain for context
 * @returns boolean | undefined - The flag value, or undefined if not yet loaded
 */
export function useFeatureFlag(flagKey: string, subdomain: string): boolean | undefined {
  const launchDarklyClient = useLDClient()
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    if (isLocalEnv) {
      setEnabled(LOCAL_FLAG_OVERRIDES[flagKey] ?? true)
      return
    }

    if (!launchDarklyClient || !subdomain) return

    const checkFlag = async () => {
      try {
        await launchDarklyClient.waitForInitialization()

        const context = createTenantContext(subdomain)
        await launchDarklyClient.identify(context)

        const detail: LDEvaluationDetail = launchDarklyClient.variationDetail(flagKey, false)
        const isEnabled = typeof detail.value === 'boolean' ? detail.value : false

        setEnabled(isEnabled)
      } catch (error) {
        console.error('Feature flag identification error:', error)
      }
    }

    checkFlag()
  }, [flagKey, subdomain, launchDarklyClient])

  return enabled
}
